import React from "react";
import { Link } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { Heart, Star } from "lucide-react";

const COLOR_HEX = {
  black: "#111827", white: "#f8fafc", navy: "#1e3a5f", blue: "#2563eb", "sky blue": "#7dd3fc",
  grey: "#9ca3af", gray: "#9ca3af", "off white": "#f5f5f0", beige: "#e3d5b8", khaki: "#b8a265",
  olive: "#708238", green: "#16a34a", maroon: "#7f1d1d", teal: "#0d9488", blush: "#f6c6c6",
  coral: "#ff7f50", yellow: "#facc15", "light blue": "#93c5fd", multi: "#a855f7", cream: "#fdf6e3",
};

export default function ProductCard({ product, index = 0 }) {
  const { formatPrice, toggleWishlist, wishlist, addToCart } = useApp();
  const inWishlist = wishlist.includes(product.id);
  const discount = product.original_price
    ? Math.round((1 - product.price / product.original_price) * 100)
    : 0;

  return (
    <div
      data-testid="product-card-item"
      className="group relative bg-white dark:bg-slate-900 rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-800 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl animate-fade-up"
      style={{ animationDelay: `${(index % 8) * 60}ms` }}
    >
      <Link to={`/product/${product.id}`} className="block relative overflow-hidden aspect-[3/4] bg-slate-100">
        <img
          src={product.images?.[0]}
          alt={product.name}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out"
        />
        {discount > 0 && (
          <span className="absolute top-3 left-3 bg-[#E63946] text-white text-xs font-bold px-2 py-1 rounded-md font-mono-price">
            {discount}% OFF
          </span>
        )}
        {product.featured && (
          <span className="absolute top-3 left-3 mt-7 bg-[#D4AF37] text-[#1D3557] text-[10px] font-bold px-2 py-0.5 rounded-md uppercase tracking-wide">
            Bestseller
          </span>
        )}
        <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-white/85 backdrop-blur-sm rounded-full pl-1 pr-2 py-0.5 shadow-sm">
          <img src="/brand/bull-mark.png" alt="" className="h-4 w-auto" />
          <span className="text-[9px] font-extrabold text-[#1D3557] tracking-wider">BULL</span>
        </div>
      </Link>

      <button
        data-testid="product-wishlist-button"
        onClick={() => toggleWishlist(product.id)}
        className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 dark:bg-slate-800/90 backdrop-blur flex items-center justify-center shadow-sm hover:scale-110 transition-transform"
        aria-label="wishlist"
      >
        <Heart size={17} className={inWishlist ? "fill-[#E63946] text-[#E63946]" : "text-slate-500"} />
      </button>

      <div className="p-4">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
            {product.gender} • {product.category}
          </span>
          <span className="flex items-center gap-0.5 text-xs font-semibold text-emerald-700">
            <Star size={12} className="fill-emerald-600 text-emerald-600" /> {product.rating}
          </span>
        </div>
        <Link to={`/product/${product.id}`}>
          <h3 className="font-semibold text-[#0F172A] dark:text-white text-sm leading-snug line-clamp-1 hover:text-[#E63946] transition-colors">
            {product.name}
          </h3>
        </Link>
        <p className="text-xs text-slate-400 mb-3 line-clamp-1">{product.fabric} • {product.fit}</p>

        {product.colors?.length > 0 && (
          <div className="flex items-center gap-1.5 mb-2">
            {product.colors.slice(0, 5).map((c) => (
              <span key={c} title={c} className="w-3.5 h-3.5 rounded-full border border-slate-300" style={{ background: COLOR_HEX[c.toLowerCase()] || "#cbd5e1" }} />
            ))}
            {product.colors.length > 1 && <span className="text-[10px] text-slate-400 ml-0.5">{product.colors.length} colours</span>}
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="flex items-baseline gap-1.5">
            <span className="font-mono-price font-bold text-[#0F172A] dark:text-white">
              {formatPrice(product.price)}
            </span>
            {product.original_price && (
              <span className="text-xs text-slate-400 line-through font-mono-price">
                {formatPrice(product.original_price)}
              </span>
            )}
          </div>
        </div>

        <button
          data-testid="product-quick-add-button"
          onClick={() => addToCart(product, product.sizes?.[1] || product.sizes?.[0] || "M", product.colors?.[0] || "Default")}
          className="mt-3 w-full py-2 rounded-lg bg-[#E63946] text-white text-sm font-semibold hover:bg-[#d12836] active:scale-[0.98] transition-all"
        >
          Add to Bag
        </button>
      </div>
    </div>
  );
}
